import React, { useEffect, useState, useContext, useMemo } from "react";
import { useRouter } from "next/router";

import { useShopContext } from "../../context/ShopContext";
import { useUser } from "../../context/UserContext";

import SimpleProductCard from "../product/SimpleProductCard";
import AccountSubHeader from "../account/accountSubHeader/accountSubheader";
import Button from "../button/Button";
import { CartItem, OrderStatusTypes } from "../../commonTypes";

import styles from "./yourWardrobe.module.css";
import moment from "moment";
import CancelSubscriptionModal from "../modal/cancelSubscriptionModal/cancelSubscriptionModal";
import KeepItemModal from "../modal/keepItemModal/KeepItemModal";
import InternalLink from "../../components/InternalLink";

import { ModalContext } from "../../context/ModalContext";
import ICONS from "../constants/icons";
import { $Enums } from "@prisma/client";

const YourWardrobe = ({
  wardrobe,
  deliveryDate,
  exchangedWardrobe,
  returnDate,
  returnedItems,
  keptItems,
  subscriptionCancelled,
}: {
  wardrobe: CartItem[];
  deliveryDate: Date;
  returnDate?: Date;
  exchangedWardrobe?: boolean;
  returnedItems?: boolean;
  keptItems?: boolean;
  subscriptionCancelled?: boolean;
}): JSX.Element => {
  const [buttonText, setButtonText] = useState(null);
  const { rotateCart } = useShopContext();
  const { userProfile } = useUser();
  const router = useRouter();
  const { handleModal } = useContext(ModalContext);
  // const hasActiveReturn = userProfile.

  const hasOngoingReturn = useMemo(() => {
    return !!userProfile.partial_return.find(
      (itm) => itm.status !== $Enums.OrderStatus.ORDER_FINALISED
    );
  }, []);

  // const findMultipleActiveOrders = () => {
  //   const hasActiveOrder = userProfile?.order?.find((ord) => {
  //     return ord.status === OrderStatusTypes.ACTIVE;
  //   });

  //   const hasPlacedOrder = userProfile?.order?.find((ord) => {
  //     return ord.status === OrderStatusTypes.PLACED;
  //   });

  //   const hasDoubleActiveOrder = userProfile?.order?.filter((ord) => {
  //     if (ord.status === OrderStatusTypes.ACTIVE) return ord;
  //   });

  //   if (
  //     (hasActiveOrder && hasPlacedOrder) ||
  //     hasDoubleActiveOrder?.length > 1
  //   ) {
  //     return true;
  //   }

  //   return false;
  // };

  // const hasMultipleActiveOrder = useMemo(() => {
  //   return findMultipleActiveOrders();
  // }, [userProfile]);

  useEffect(() => {
    setButtonText("Rotate");
  }, []);

  const hasDelivered = moment(deliveryDate).isBefore() || !deliveryDate;

  const renderHeader = () => {
    // renders correct header for section
    if (returnedItems) {
      return "Items being returned";
    }
    if (keptItems) {
      return "Items being kept";
    }
    if (hasDelivered && returnDate) {
      return "Your Wardrobe (Subscription Cancelled)";
    } else if (hasDelivered && !returnDate) {
      return `Your ${exchangedWardrobe ? "Current" : ""} Wardrobe`;
    } else {
      return "Upcoming Wardrobe";
    }
  };

  const handleRotate = () => {
    if (hasOngoingReturn) {
      handleModal({
        content: (
          <div className={styles.modalContainer}>
            <ICONS.ORLOGO size={100} />
            <div>
              You will be able to rotate as soon as your return has been
              processed
            </div>
          </div>
        ),
      });
    } else {
      //Open keep item modal
      handleModal({
        content: <KeepItemModal activeOrderItems={wardrobe} />,
        withImage: true,
      });
    }
  };

  const handleChangePlan = () => {
    //adds current wardrobe to basket, redirects user to collection page to change stuff?
    //! Does this take into account stock levels??
    rotateCart(wardrobe);
    router.push("/collection?select_plan=true");
  };

  const handleReturnCancel = () => {
    handleModal({
      content: <CancelSubscriptionModal />,
      withImage: true,
    });
    //TODO - handle subscription cancellation + setup pickup date + change order status
  };

  return (
    <div>
      <AccountSubHeader
        title={
          returnedItems
            ? "Items being returned"
            : keptItems
            ? "Items being kept"
            : hasDelivered
            ? returnDate
              ? "Your Wardrobe"
              : `Your ${exchangedWardrobe ? "Current" : ""} Wardrobe`
            : "Upcoming Wardrobe"
        }
      >
        {!keptItems && !returnedItems && wardrobe?.length > 0 && (
          <div className={styles.buttons}>
            {subscriptionCancelled ? (
              <Button
                onClick={() => router.push("/collection?select_plan=true")}
                text={"Start rotating again"}
              />
            ) : (
              <>
                <Button
                  onClick={handleReturnCancel}
                  text="Return & cancel"
                  disabled={!hasDelivered}
                />

                <Button
                  onClick={handleRotate}
                  text={buttonText}
                  disabled={
                    buttonText === "Wardrobe added to cart" || !hasDelivered
                  }
                />
                <Button
                  onClick={handleChangePlan}
                  text="Change my plan"
                  disabled={!hasDelivered}
                />
              </>
            )}
          </div>
        )}
        {!hasDelivered && deliveryDate && (
          <p className={styles.deliveryDate}>
            Delivery on {moment(deliveryDate).format("Do MMM")}{" "}
            <InternalLink href="/contact">
              <a>Change</a>
            </InternalLink>
          </p>
        )}
        {hasDelivered && returnDate && (
          <p className={styles.deliveryDate}>
            Pickup on {moment(returnDate).format("Do MMM")}{" "}
            <InternalLink href="/contact">
              <a>Change</a>
            </InternalLink>
          </p>
        )}
      </AccountSubHeader>
      <div>
        {wardrobe?.length < 1 ? (
          <p>No active orders found</p>
        ) : (
          <div className={styles.products}>
            {(wardrobe || []).map((item: CartItem, index) => {
              if (!item.product) {
                return <></>;
              }
              return (
                <div
                  className={styles.productItem}
                  key={`order_item${item.product.id}+${index}`}
                >
                  <SimpleProductCard cartItem={item} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default YourWardrobe;
